/**
 * Encrypts AI provider keys before they ever touch sessionStorage (the
 * browser/PWA path only -- the desktop app uses the OS keychain instead, see
 * apps/desktop/src-tauri/src/secrets.rs).
 *
 * The AES-GCM key is generated non-extractable and kept in IndexedDB: this
 * origin can use it to encrypt/decrypt, but its raw bytes can never be read
 * out, not even from devtools. That stops a stored key from ever being plain
 * text -- reading sessionStorage or IndexedDB directly only gets you
 * ciphertext. It does not, and cannot, protect against a live XSS in this
 * same origin: that attacker can call the same decrypt function the app
 * does. No purely client-side scheme can close that gap; the desktop app's
 * OS keychain has no such ceiling.
 */

const DB_NAME = 'nexus-open-keystore';
const STORE = 'keys';
const KEY_ID = 'master';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGet(db: IDBDatabase): Promise<CryptoKey | undefined> {
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(KEY_ID);
    req.onsuccess = () => resolve(req.result as CryptoKey | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db: IDBDatabase, key: CryptoKey): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(key, KEY_ID);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

let masterKey: Promise<CryptoKey> | null = null;

/** The one AES-GCM key for this browser profile: made once, non-extractable, reused after that. */
function getMasterKey(): Promise<CryptoKey> {
  if (!masterKey) {
    masterKey = (async () => {
      const db = await openDb();
      const existing = await idbGet(db);
      if (existing) return existing;
      const fresh = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
        'encrypt',
        'decrypt',
      ]);
      await idbPut(db, fresh);
      return fresh;
    })();
  }
  return masterKey;
}

const toB64 = (bytes: Uint8Array): string => btoa(String.fromCharCode(...bytes));
const fromB64 = (s: string): Uint8Array<ArrayBuffer> => {
  const bin = atob(s);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
};

/** Encrypts a value for storage. The result is ciphertext; safe to keep as a plain string. */
export async function encryptForStorage(value: string): Promise<string> {
  const key = await getMasterKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(value),
  );
  return `${toB64(iv)}.${toB64(new Uint8Array(ct))}`;
}

/** Reverses encryptForStorage. Null if missing, malformed, or undecryptable (e.g. another profile). */
export async function decryptFromStorage(stored: string): Promise<string | null> {
  try {
    const [ivB64, ctB64] = stored.split('.');
    if (!ivB64 || !ctB64) return null;
    const key = await getMasterKey();
    const pt = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromB64(ivB64) },
      key,
      fromB64(ctB64),
    );
    return new TextDecoder().decode(pt);
  } catch {
    return null;
  }
}
